import { useMemo, useState } from 'react';
import { Mail, MessageCircle, Linkedin, Twitter, Link2, Check, Gift } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Compact "Refer a colleague" share sheet rendered on the Dashboard.
 *
 * Why it exists: mental-health SaaS sees the bulk of its signups from peer
 * recommendations. A pre-filled, one-click share card on the dashboard
 * turns happy providers into a low-friction acquisition channel.
 *
 * No backend work required — the referral URL carries the current user's
 * id in a `?ref=` query param so later we can attribute sign-ups if we
 * decide to add reward logic.
 */
export default function ReferColleagueCard({ user }) {
  const [copied, setCopied] = useState(false);

  const firstName = (user?.full_name || user?.email || '').split(/[\s@]/)[0] || 'A colleague';

  const referralUrl = useMemo(() => {
    const base = `${window.location.protocol}//${window.location.host}`;
    const ref = user?.id ? `?ref=${encodeURIComponent(user.id)}` : '';
    return `${base}${ref}`;
  }, [user?.id]);

  const message = useMemo(
    () =>
      `${firstName} thought you'd love Plexura AI Scribe — I just cut my note-writing time by 60%. It records sessions, drafts SOAP + psychotherapy notes, and pushes straight into my EHR. Worth a try:`,
    [firstName],
  );

  const shareText = `${message} ${referralUrl}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast.success('Invite copied — paste it anywhere');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Could not copy — please copy manually');
    }
  };

  const openWindow = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer,width=640,height=720');
  };

  const channels = [
    {
      key: 'email',
      label: 'Email',
      icon: Mail,
      onClick: () => {
        const subject = encodeURIComponent('You should try Plexura AI Scribe');
        const body = encodeURIComponent(`${message}\n\n${referralUrl}\n\n— ${firstName}`);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
      },
      color: '#2C4A3B',
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageCircle,
      onClick: () => openWindow(`https://wa.me/?text=${encodeURIComponent(shareText)}`),
      color: '#25D366',
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      icon: Linkedin,
      onClick: () =>
        openWindow(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralUrl)}`,
        ),
      color: '#0A66C2',
    },
    {
      key: 'twitter',
      label: 'X / Twitter',
      icon: Twitter,
      onClick: () =>
        openWindow(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`,
        ),
      color: '#111827',
    },
  ];

  return (
    <div
      className="rounded-2xl border border-white/60 bg-white/80 backdrop-blur p-4 sm:p-5 shadow-sm"
      data-testid="refer-colleague-card"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: 'linear-gradient(135deg, #F0E7D8, #E3F1E8)' }}
          >
            <Gift className="w-5 h-5" style={{ color: '#2C4A3B' }} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold" style={{ color: '#1E3329' }} data-testid="refer-title">
              Know a clinician who still types notes?
            </p>
            <p className="text-xs text-gray-600 mt-0.5" data-testid="refer-subtitle">
              Share Plexura — they get 60% of their evenings back.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap sm:justify-end">
          {channels.map(({ key, label, icon: Icon, onClick, color }) => (
            <button
              key={key}
              type="button"
              onClick={onClick}
              data-testid={`refer-share-${key}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors text-xs font-medium text-gray-700"
              aria-label={`Share via ${label}`}
              title={`Share via ${label}`}
            >
              <Icon className="w-3.5 h-3.5" style={{ color }} />
              <span className="hidden md:inline">{label}</span>
            </button>
          ))}
          <button
            type="button"
            onClick={handleCopy}
            data-testid="refer-share-copy"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors"
            style={{
              background: copied ? '#E3F1E8' : '#2C4A3B',
              borderColor: copied ? '#2C4A3B' : '#2C4A3B',
              color: copied ? '#1E3329' : 'white',
            }}
            aria-label="Copy invite link"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
            {copied ? 'Copied' : 'Copy invite'}
          </button>
        </div>
      </div>
    </div>
  );
}
